/**
 * Move contract error mappings
 * Maps error codes to user-friendly messages
 */
// Error mappings by module
export const MOVE_ERROR_MAP = {
    // Pyth price oracle
    pyth: {
        1: 'Invalid price data provided.',
        2: 'Price feed not found.',
        3: 'Price data is not fresh. Please try again in a moment.',
        4: 'Price confidence interval too wide.',
        5: 'Price data is stale.',
    },
    // Alpha lending protocol
    alpha_lending: {
        0: 'Withdrawal would leave your position undercollateralized, which is not allowed.',
        1: 'The borrow amount that you requested exceeds safe borrow limits.',
        2: 'Cannot borrow from isolated market with other borrows active.',
        3: 'You are not authorized to perform this action.',
        4: 'Position is not eligible for liquidation.',
        7: 'Invalid contract version. Please refresh and try again.',
        8: 'AlphaLend upgrade in progress. Please try after some time.',
        29: "Cannot supply into a market where you've borrowed.",
        30: "Cannot borrow from a market where you've supplied.",
        31: 'Enter a valid amount.',
        32: 'Enter a valid amount.',
        33: 'Enter a valid amount.',
        37: 'Market supply limit reached.',
    },
    // Flow limiter module
    flow_limiter: {
        0: 'The market flow limit has been reached. Please lower the amount or try again later.',
        '13906834436336386049': 'The market flow limit has been reached. Please lower the amount or try again later.',
    },
    // Market module
    market: {
        30: 'Market borrow limit reached.',
    },
    // Oracle module
    oracle: {
        0: 'Outdated Oracle Price',
    },
    // Generic fallback
    generic: {
        0: 'Transaction failed due to an unknown error.',
        1: 'Insufficient funds for transaction.',
        2: 'Invalid parameters provided.',
        3: 'Operation not permitted.',
    },
    // Slush pools (alphalend_slush). Both modules share the same code space for the
    // user-facing failures; the deprecated marker differs (1018 vs 1035) because the
    // locked-loop pool retired a different function.
    alphalend_slush_pool: {
        1011: 'This pool is paused. Please try again later.',
        1012: 'Deposit amount is too small.',
        1013: 'No position found in this pool for your wallet.',
        1014: 'Withdrawal amount exceeds your vault balance.',
        1015: 'Enter a valid amount.',
        1018: 'This action is no longer supported. Please refresh and try again.',
        1019: 'Deposit would reduce the pool collateral. Please try again.',
    },
    alphalend_slush_locked_loop_pool: {
        1011: 'This pool is paused. Please try again later.',
        1012: 'Deposit amount is too small.',
        1013: 'No position found in this pool for your wallet.',
        1014: 'Withdrawal amount exceeds your vault balance.',
        1015: 'Enter a valid amount.',
        1020: 'Withdrawal request not found. Please refresh and try again.',
        1023: 'This withdrawal is still locked. Please wait until the unlock time.',
        1025: 'This withdrawal request has not been accepted yet.',
        1032: 'Pool deposit limit reached.',
        1035: 'This action is no longer supported. Please refresh and try again.',
    },
    // Position module
    position: {
        0: 'Position is not eligible for liquidation.',
        1: 'Incorrect asset used for liquidation.',
        2: "Error when accessing a position that hasn't been refreshed.",
        3: 'Withdrawal amount exceeds your supply balance.',
    },
};
// Module name aliases — only non-identity mappings are needed here;
// unknown names fall back to the raw module name automatically.
const MODULE_ALIASES = {
    lending: 'alpha_lending',
    alphalend: 'alpha_lending',
    price_oracle: 'oracle',
};
/**
 * Gas failures, matched case-insensitively because the spelling varies by transport:
 * 'InsufficientGas' (JSON-RPC), 'Insufficient Gas.' (ExecutionFailureStatus Display),
 * 'INSUFFICIENT_GAS' (gRPC enum). Mirrors the word-boundary patterns txResult.ts uses for
 * `isGasError`, kept in sync deliberately — a form matched there but missed here would fall
 * through to the generic message, which is the bug this map exists to prevent.
 *
 * Only consulted for messages that are NOT Move aborts. `isGasError` pairs these patterns
 * with a `$kind` deny-list to avoid reclassifying a structured failure that merely mentions
 * gas; here the equivalent guard is the `isMoveAbortError` check at the call site, so an
 * abort in a function or constant named `…insufficient_gas…` still gets its code parsed.
 *
 * The two cases are distinct: the budget was exhausted mid-execution, or the sender could
 * not cover the budget up front (a pinned budget larger than their gas balance).
 */
const GAS_ERROR_PATTERNS = [
    // Separators are optional throughout: Sui emits this as 'GasBalanceTooLow',
    // 'GAS_BALANCE_TOO_LOW' or 'gas balance too low' depending on transport. Requiring
    // literal spaces matched none of them. (The validator's input-check phrasing,
    // 'Balance of gas object N is lower than the needed amount', is caught by the
    // 'is lower than the needed amount' key in NON_MOVE_ERROR_MAP instead.)
    [
        /\bgas[\s_-]*balance[\s_-]*too[\s_-]*low\b/i,
        'Not enough SUI to cover transaction fees. Please top up and retry.',
    ],
    [
        /\binsufficient[\s_-]*gas\b/i,
        'Transaction ran out of gas. Please ensure you have enough SUI and retry.',
    ],
];
const NON_MOVE_ERROR_MAP = {
    InsufficientCoinBalance: 'Insufficient balance for this transaction.',
    'is lower than the needed amount': 'Insufficient balance for transaction fees.',
    'User rejected the request': 'Transaction was cancelled by user.',
    'Transaction was not executed': 'Transaction was not executed. Please try again.',
    rejected: 'Transaction was cancelled by user.',
    cancelled: 'Transaction was cancelled by user.',
};
const GENERIC_MOVE_PARSE_FAILURE = 'Transaction failed due to an unexpected error. Please try again.';
/**
 * Parse Move abort error to extract module and error code
 */
export function parseMoveAbortError(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string') {
        return { module: null, errorCode: null, errorCodeString: null, isValid: false };
    }
    try {
        let moduleMatch = errorMessage.match(/name: Identifier\("([^"]+)"\)/);
        if (!moduleMatch) {
            moduleMatch = errorMessage.match(/name: "([^"]+)"/);
        }
        if (!moduleMatch) {
            moduleMatch = errorMessage.match(/::(\w+)::\w+(?:<[^']*>)?'/);
        }
        let errorCodeMatch = errorMessage.match(/\}, (\d+)\)/);
        if (!errorCodeMatch) {
            errorCodeMatch = errorMessage.match(/MoveAbort\([^,]+,\s*(\d+)\)/);
        }
        if (!errorCodeMatch) {
            errorCodeMatch = errorMessage.match(/abort code:\s*(\d+)/);
        }
        const rawModule = moduleMatch ? moduleMatch[1] : null;
        const module = rawModule ? MODULE_ALIASES[rawModule.toLowerCase()] || rawModule : null;
        const errorCodeString = errorCodeMatch ? errorCodeMatch[1] : null;
        const errorCode = errorCodeString ? parseInt(errorCodeString, 10) : null;
        const isValidErrorCode = errorCode !== null && !isNaN(errorCode) && errorCode >= 0;
        return {
            module,
            errorCode: isValidErrorCode ? errorCode : null,
            errorCodeString: isValidErrorCode ? errorCodeString : null,
            isValid: module !== null && isValidErrorCode,
        };
    }
    catch (_a) {
        return { module: null, errorCode: null, errorCodeString: null, isValid: false };
    }
}
/**
 * Get user-friendly error message
 */
export function getMoveErrorMessage(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string') {
        return GENERIC_MOVE_PARSE_FAILURE;
    }
    // Non-Move failures first — and only when the message genuinely isn't a Move abort, which
    // is what "non-Move" means. Every entry below matches loosely (bare substrings, or regexes
    // over words that also occur in Move identifiers), while an abort message embeds the
    // aborting function's name. Testing them against an abort would misreport it and skip
    // parsing the code entirely: an abort from `assert_not_cancelled` would read "Transaction
    // was cancelled by user", and one from `assert_insufficient_gas` would read as a gas
    // failure. One gate, so there is a single place that decides "this isn't an abort".
    if (!isMoveAbortError(errorMessage)) {
        for (const [pattern, userMessage] of GAS_ERROR_PATTERNS) {
            if (pattern.test(errorMessage)) {
                return userMessage;
            }
        }
        for (const [errorPattern, userMessage] of Object.entries(NON_MOVE_ERROR_MAP)) {
            if (errorMessage.includes(errorPattern)) {
                return userMessage;
            }
        }
        return GENERIC_MOVE_PARSE_FAILURE;
    }
    const { module, errorCode, errorCodeString, isValid } = parseMoveAbortError(errorMessage);
    if (!isValid || errorCode === null) {
        return GENERIC_MOVE_PARSE_FAILURE;
    }
    // Check specific module first
    if (module && errorCodeString) {
        const moduleMap = MOVE_ERROR_MAP[module];
        if (moduleMap === null || moduleMap === void 0 ? void 0 : moduleMap[errorCodeString]) {
            return moduleMap[errorCodeString];
        }
        if (moduleMap === null || moduleMap === void 0 ? void 0 : moduleMap[errorCode]) {
            return moduleMap[errorCode];
        }
    }
    // Fall back to scanning other modules ONLY when this module is unknown to the map. If we
    // do know the module and it simply has no entry for this code, another module's text for
    // the same number is not about this failure — the codes are per-module and collide freely
    // (e.g. alpha_lending 1014 would otherwise render the Slush pool's "exceeds your vault
    // balance"). Better a generic message than a confidently wrong one.
    if (!module || !MOVE_ERROR_MAP[module]) {
        for (const [mod, moduleMap] of Object.entries(MOVE_ERROR_MAP)) {
            if (mod === 'generic')
                continue;
            // Try both string and number lookup
            if (errorCodeString && moduleMap[errorCodeString]) {
                return moduleMap[errorCodeString];
            }
            if (moduleMap[errorCode]) {
                return moduleMap[errorCode];
            }
        }
    }
    // Fallback to generic
    if (errorCodeString && MOVE_ERROR_MAP.generic[errorCodeString]) {
        return MOVE_ERROR_MAP.generic[errorCodeString];
    }
    if (MOVE_ERROR_MAP.generic[errorCode]) {
        return MOVE_ERROR_MAP.generic[errorCode];
    }
    return GENERIC_MOVE_PARSE_FAILURE;
}
/**
 * Pull the message out of a caught value.
 *
 * Not every throwable is an `Error`: a wallet extension can reject with a plain payload like
 * `{ code: -32002, message: 'Request already pending' }`, and an `Error` that crosses a realm
 * boundary (extension → page) fails `instanceof`. `String(value)` on either yields
 * "[object Object]", losing the one string the error maps can match on.
 */
export function getRawErrorMessage(rawError) {
    if (rawError instanceof Error)
        return rawError.message;
    if (typeof rawError === 'string')
        return rawError;
    if (typeof rawError === 'object' && rawError !== null) {
        const { message } = rawError;
        // No usable message: return '' rather than "[object Object]". That string is truthy, so it
        // would swallow the caller's `|| fallbackMessage` and leave a fake message in `raw`. The
        // object itself is still attached to the log, which is where its `code` is readable.
        return typeof message === 'string' ? message : '';
    }
    return String(rawError !== null && rawError !== void 0 ? rawError : '');
}
/**
 * Log a transaction failure that is being shown to the user.
 *
 * Every failure toast goes through a handler that maps the raw error to a friendly message
 * and then discards the original, so a report like "Price data is not fresh." arrived with
 * nothing in the console to trace it back to — worst on the dry-run path, which failed
 * entirely silently. Logging raw and shown together, from the same place that builds the
 * toast, means every notification the user sees has a matching console entry.
 *
 * Accepts `unknown` so callers can hand over the caught value as-is. Pass `displayError`
 * whenever the caller maps the message itself, so `shown` is the string the toast really
 * used rather than a second, independent mapping of the same input.
 */
export function logTransactionError(action, rawError, displayError) {
    const rawMessage = getRawErrorMessage(rawError);
    console.error(`[tx] ${action} failed`, Object.assign({ raw: rawMessage, shown: displayError !== null && displayError !== void 0 ? displayError : getMoveErrorMessage(rawMessage) }, (typeof rawError === 'object' && rawError !== null && { error: rawError })));
}
/**
 * Check if error is a Move abort error
 */
export function isMoveAbortError(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string' || errorMessage.trim() === '') {
        return false;
    }
    return errorMessage.includes('MoveAbort');
}
